/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { PointerEvent } from 'react';
import { flushSync } from 'react-dom';
import {
  layoutTiles,
  moveSeam,
  type Rect,
  seamKey,
  setSplitWeights,
  type TileDropTarget,
  type TileNode,
  type TileSeam,
} from './tileTree';

/*
 * Pointer interactions write CSS variables straight onto the window elements, at most once per
 * frame, and commit to state once on release: React renders nothing while the pointer moves.
 */

/** Pointer travel before a press on the title bar becomes a drag (and a tile lifts out). */
const DRAG_THRESHOLD_PX = 6;
/** A snap target must hold this long before its preview shows, so sweeping doesn't flicker. */
const SNAP_SETTLE_MS = 150;

export interface WindowSnap {
  findTarget: (point: { x: number; y: number }) => TileDropTarget | null;
  previewRect: (target: TileDropTarget) => Rect | undefined;
}

/** Calls `apply` with the latest pointer event once per animation frame. */
const perFrame = (apply: (event: globalThis.PointerEvent) => void) => {
  let frame = 0;
  let latest: globalThis.PointerEvent | null = null;
  return {
    schedule(event: globalThis.PointerEvent) {
      latest = event;
      frame ||= requestAnimationFrame(() => {
        frame = 0;
        if (latest) apply(latest);
      });
    },
    /** Applies the pending event now (on release) and stops. */
    flush() {
      cancelAnimationFrame(frame);
      frame = 0;
      if (latest) apply(latest);
      latest = null;
    },
    /** Drops the pending event (on cancel), so it can't reapply styles after cleanup. */
    cancel() {
      cancelAnimationFrame(frame);
      frame = 0;
      latest = null;
    },
  };
};

/** Pointer listeners for one press, removed on release, cancel or Escape. */
const trackPointer = (
  event: PointerEvent<HTMLElement>,
  {
    onMove,
    onEnd,
  }: { onMove: (event: globalThis.PointerEvent) => void; onEnd: (cancelled: boolean) => void },
) => {
  const { pointerId } = event;
  event.currentTarget.setPointerCapture?.(pointerId);
  const handleMove = (moveEvent: globalThis.PointerEvent) => {
    if (moveEvent.pointerId === pointerId) onMove(moveEvent);
  };
  const end = (cancelled: boolean) => {
    window.removeEventListener('pointermove', handleMove);
    window.removeEventListener('pointerup', handleUp);
    window.removeEventListener('pointercancel', handleUp);
    window.removeEventListener('keydown', handleKey, true);
    onEnd(cancelled);
  };
  const handleUp = (upEvent: globalThis.PointerEvent) => {
    if (upEvent.pointerId === pointerId) end(upEvent.type === 'pointercancel');
  };
  const handleKey = (keyEvent: KeyboardEvent) => {
    if (keyEvent.key !== 'Escape') return;
    keyEvent.preventDefault();
    keyEvent.stopPropagation();
    end(true);
  };
  window.addEventListener('pointermove', handleMove);
  window.addEventListener('pointerup', handleUp);
  window.addEventListener('pointercancel', handleUp);
  window.addEventListener('keydown', handleKey, true);
};

export function startWindowDrag({
  event,
  start,
  restoreSize,
  snap,
  onDrop,
}: {
  event: PointerEvent<HTMLElement>;
  start: Rect;
  /** A tiled window's own size: it takes it once the drag lifts it out of its tile. */
  restoreSize?: { width: number; height: number };
  snap?: WindowSnap;
  onDrop: (x: number, y: number, target?: TileDropTarget) => void;
}) {
  const windowElement = event.currentTarget.closest<HTMLElement>('[role="dialog"]');
  const layer = windowElement?.parentElement;
  if (!windowElement || !layer) return;
  const layerRect = layer.getBoundingClientRect();
  const preview = layer.querySelector<HTMLElement>('[data-tile-preview]');
  const edges = getDesktopEdge(windowElement);
  const origin = { x: event.clientX, y: event.clientY };
  // Where the pointer holds the window, kept as the window changes size.
  let grab = { x: origin.x - layerRect.left - start.x, y: origin.y - layerRect.top - start.y };
  let size = { width: start.width, height: start.height };
  let next = { x: start.x, y: start.y };
  let lifted = false;
  let candidate: TileDropTarget | null = null;
  let settleTimer: number | undefined;
  setInteractionMode(windowElement, true);
  // The size cap follows left/top, which a transform drag leaves behind: without this a tile
  // lifted out at its own (wider) size would stay narrow until release, then jump.
  windowElement.style.setProperty('--desktop-window-room-x', '100%');
  windowElement.style.setProperty('--desktop-window-room-y', '100%');

  const showPreview = (target: TileDropTarget | null) => {
    const rect = target && snap?.previewRect(target);
    if (!preview) return;
    preview.dataset.visible = rect ? 'true' : 'false';
    if (rect) setRectVars(preview, rect);
  };

  const frame = perFrame((moveEvent) => {
    if (!lifted) {
      if (
        Math.hypot(moveEvent.clientX - origin.x, moveEvent.clientY - origin.y) < DRAG_THRESHOLD_PX
      )
        return;
      lifted = true;
      if (restoreSize) {
        grab = { x: (grab.x / size.width) * restoreSize.width, y: grab.y };
        size = restoreSize;
        windowElement.style.setProperty('--desktop-window-width', `${size.width}px`);
        windowElement.style.setProperty('--desktop-window-height', `${size.height}px`);
      }
    }
    const pointer = { x: moveEvent.clientX - layerRect.left, y: moveEvent.clientY - layerRect.top };
    next = {
      x: clamp(
        pointer.x - grab.x,
        -layerRect.left,
        Math.max(0, edges.right - layerRect.left - size.width),
      ),
      y: clamp(
        pointer.y - grab.y,
        -layerRect.top,
        Math.max(0, edges.bottom - layerRect.top - size.height),
      ),
    };
    windowElement.style.setProperty('--desktop-window-dx', `${next.x - start.x}px`);
    windowElement.style.setProperty('--desktop-window-dy', `${next.y - start.y}px`);
    const target = snap?.findTarget(pointer) ?? null;
    if (JSON.stringify(target) === JSON.stringify(candidate)) return;
    candidate = target;
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(() => showPreview(candidate), SNAP_SETTLE_MS);
  });

  trackPointer(event, {
    onMove: frame.schedule,
    onEnd: (cancelled) => {
      if (cancelled) frame.cancel();
      else frame.flush();
      window.clearTimeout(settleTimer);
      showPreview(null);
      // Swap the drag transform for left/top while transitions are still off: transitioning
      // both at once (transform back on the compositor, left/top forward on the main thread)
      // drifts apart and shows the window sliding back before it catches up.
      if (lifted) {
        windowElement.style.setProperty('--desktop-window-left', `${next.x}px`);
        windowElement.style.setProperty('--desktop-window-top', `${next.y}px`);
        windowElement.style.removeProperty('--desktop-window-dx');
        windowElement.style.removeProperty('--desktop-window-dy');
        windowElement.getBoundingClientRect();
      }
      // Committed (or cancelled) bounds hand positioning back to state. With transitions back
      // on, the window glides from where it is to where state puts it: its snap target, or
      // back where it started when cancelled.
      if (!cancelled && lifted) onDrop(next.x, next.y, candidate ?? undefined);
      // A native layout snapshot captures the release position before committing the snap.
      // The coordinator clears these live bounds once that old picture has been captured.
      if (!windowElement.hasAttribute('data-layout-snapshot-pending'))
        setInteractionMode(windowElement, false);
    },
  });
}

export function startWindowResize({
  event,
  onResize,
  startHeight,
  startWidth,
  axis = 'both',
  startX,
  startY,
}: {
  event: PointerEvent<HTMLElement>;
  onResize: (width: number, height: number) => void;
  startHeight: number;
  startWidth: number;
  axis?: 'both' | 'width' | 'height';
  startX: number;
  startY: number;
}) {
  const originX = event.clientX;
  const originY = event.clientY;
  const windowElement = event.currentTarget.closest<HTMLElement>('[role="dialog"]');
  const resizeBounds = getWindowResizeBounds(windowElement, startX, startY);
  const renderedBounds = windowElement?.getBoundingClientRect();
  startWidth = renderedBounds?.width || startWidth;
  startHeight = renderedBounds?.height || startHeight;
  let nextWidth = startWidth;
  let nextHeight = startHeight;
  const restoreContent = freezeWindowContents(windowElement ? [windowElement] : []);
  setInteractionMode(windowElement, true);

  const frame = perFrame((moveEvent) => {
    if (axis !== 'height')
      nextWidth = clamp(
        startWidth + moveEvent.clientX - originX,
        280,
        Math.max(280, resizeBounds.maxWidth),
      );
    if (axis !== 'width')
      nextHeight = clamp(
        startHeight + moveEvent.clientY - originY,
        220,
        Math.max(220, resizeBounds.maxHeight),
      );
    windowElement?.style.setProperty('--desktop-window-width', `${nextWidth}px`);
    windowElement?.style.setProperty('--desktop-window-height', `${nextHeight}px`);
  });

  trackPointer(event, {
    onMove: frame.schedule,
    onEnd: (cancelled) => {
      if (cancelled) frame.cancel();
      else frame.flush();
      try {
        // Commit before removing the live bounds, while transitions and content resize are held.
        // Otherwise release animates the heavy content from the old size to the new one again.
        if (!cancelled) flushSync(() => onResize(nextWidth, nextHeight));
      } finally {
        setInteractionMode(windowElement, false, cancelled);
        restoreContent();
      }
    },
  });
}

/** Drags the divider between two tiles: both neighbours (and everything inside them) follow. */
export function startSeamDrag({
  event,
  seam,
  layout,
  bounds,
  onCommit,
}: {
  event: PointerEvent<HTMLElement>;
  seam: TileSeam;
  layout: TileNode;
  bounds: Rect;
  onCommit: (weights: number[]) => void;
}) {
  const handle = event.currentTarget;
  const layer = handle.parentElement;
  const origin = seam.axis === 'row' ? event.clientX : event.clientY;
  let weights: number[] | null = null;
  const tiles = new Map<string, HTMLElement>();
  for (const element of layer?.querySelectorAll<HTMLElement>('[data-tile-window]') ?? [])
    tiles.set(element.dataset.tileWindow ?? '', element);
  // Dividers inside the resized children move with them.
  const dividers = new Map<string, HTMLElement>();
  for (const element of layer?.querySelectorAll<HTMLElement>('[data-seam-key]') ?? [])
    dividers.set(element.dataset.seamKey ?? '', element);
  const restoreContent = freezeWindowContents([...tiles.values()]);
  for (const element of tiles.values()) setInteractionMode(element, true);

  const frame = perFrame((moveEvent) => {
    const delta = (seam.axis === 'row' ? moveEvent.clientX : moveEvent.clientY) - origin;
    weights = moveSeam(layout, seam, delta);
    if (!weights) return;
    const next = layoutTiles(setSplitWeights(layout, seam.path, weights), bounds);
    for (const [id, rect] of next.leaves) {
      const element = tiles.get(id);
      if (element) setRectVars(element, rect, '--desktop-window-');
    }
    for (const moved of next.seams) {
      const divider = dividers.get(seamKey(moved));
      if (divider) setRectVars(divider, moved.rect);
    }
  });

  trackPointer(event, {
    onMove: frame.schedule,
    onEnd: (cancelled) => {
      if (cancelled) frame.cancel();
      else frame.flush();
      try {
        const committedWeights = weights;
        if (!cancelled && committedWeights) flushSync(() => onCommit(committedWeights));
      } finally {
        for (const element of tiles.values()) setInteractionMode(element, false, cancelled);
        for (const divider of dividers.values())
          for (const property of ['left', 'top', 'width', 'height'])
            divider.style.removeProperty(property);
        restoreContent();
      }
    },
  });
}

const setRectVars = (element: HTMLElement, rect: Rect, prefix?: string) => {
  const set = (name: string, value: number) =>
    prefix
      ? element.style.setProperty(`${prefix}${name}`, `${value}px`)
      : element.style.setProperty(name, `${value}px`);
  set('left', rect.x);
  set('top', rect.y);
  set('width', rect.width);
  set('height', rect.height);
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getWindowResizeBounds(windowElement: HTMLElement | null, startX: number, startY: number) {
  const containerOffset = getWindowContainerOffset(windowElement);
  const edge = getDesktopEdge(windowElement);

  return {
    maxWidth: Math.max(1, edge.right - containerOffset.left - startX),
    maxHeight: Math.max(1, edge.bottom - containerOffset.top - startY),
  };
}

/** The desktop's far edges: it narrows when a side panel (Agent) reserves space. */
function getDesktopEdge(windowElement: HTMLElement | null) {
  const desktopRect = windowElement?.parentElement?.parentElement?.getBoundingClientRect();
  return {
    right: desktopRect && desktopRect.width > 0 ? desktopRect.right : window.innerWidth,
    bottom: desktopRect && desktopRect.height > 0 ? desktopRect.bottom : window.innerHeight,
  };
}

function getWindowContainerOffset(windowElement: HTMLElement | null) {
  const parentRect = windowElement?.parentElement?.getBoundingClientRect();
  return {
    left: parentRect && parentRect.width > 0 ? parentRect.left : 0,
    top: parentRect && parentRect.height > 0 ? parentRect.top : 0,
  };
}

function setInteractionMode(
  windowElement: HTMLElement | null,
  active: boolean,
  resetImmediately = false,
) {
  if (active) {
    windowElement?.style.setProperty('--desktop-window-transition', 'none');
    return;
  }

  // Live pointer bounds were committed to state: hand positioning back to it, or later layout
  // changes (tiling, maximize/restore) would keep the stale inline size.
  for (const property of [
    '--desktop-window-dx',
    '--desktop-window-dy',
    '--desktop-window-room-x',
    '--desktop-window-room-y',
    '--desktop-window-left',
    '--desktop-window-top',
    '--desktop-window-width',
    '--desktop-window-height',
  ])
    windowElement?.style.removeProperty(property);
  // Cancellation returns to the original bounds. Resolve that once with transitions disabled;
  // otherwise re-enabling them animates heavy content through every intermediate size again.
  if (resetImmediately) windowElement?.getBoundingClientRect();
  windowElement?.style.removeProperty('--desktop-window-transition');
}

/** Hold app layout (including container units and resize observers) during pointer resizing.
 * Read every content size before writing any styles, so starting a multi-window resize needs
 * only one layout pass. The content stays mounted, keeping drafts, effects and scroll position.
 */
function freezeWindowContents(windows: HTMLElement[]) {
  const snapshots = windows.flatMap((element) => {
    const content = element.querySelector<HTMLElement>('[data-window-content]');
    if (!content) return [];
    const { width, height } = getComputedStyle(content);
    return [
      {
        content,
        width,
        height,
        previousWidth: content.style.width,
        previousHeight: content.style.height,
        previousWillChange: content.style.willChange,
      },
    ];
  });
  for (const { content, width, height } of snapshots) {
    content.style.width = width;
    content.style.height = height;
    // Retain a composited content layer while its parent clips/moves, instead of repainting
    // every SVG point and table cell. Release the layer hint with the frozen dimensions.
    content.style.willChange = 'transform';
  }
  return () => {
    for (const { content, previousWidth, previousHeight, previousWillChange } of snapshots) {
      content.style.width = previousWidth;
      content.style.height = previousHeight;
      content.style.willChange = previousWillChange;
    }
  };
}

/** Hand live pointer geometry back to React after a native snapshot has captured it. */
export function clearWindowInteraction(windowElement: HTMLElement) {
  setInteractionMode(windowElement, false);
}
